/**
 * Arranque do app.
 *
 * Ordem que importa: tema antes de tudo (senão a página pisca em branco), cliente antes
 * de qualquer chamada, e só então a sessão. A casca é criada UMA vez, quando o perfil
 * aparece, e destruída na saída — é o que mantém a assinatura da conexão viva entre
 * telas sem vazar assinaturas a cada navegação.
 */
import { h, limpar } from './dom';
import { configSupabase, ConfigAusente, ehDemonstracao } from './config';
import { iniciarTema } from './ui/tema';
import { iniciarIds } from './ui/ids';
import { criarCasca, carregando, falha } from './casca';
import { icone } from './icones';
import { protegido } from './arranque';
import type { FalhaDeArranque } from './arranque';
import type { Casca } from './casca';
import { aoNavegar, ir, iniciarRoteador, rotaAtual } from './rotas';
import type { Rota } from './rotas';
import { telaEntrada, telaSemPerfil } from './telas/entrada';
import { lerConviteDaUrl, telaConvite } from './telas/convite';
import { telaPedidos } from './telas/pedidos';
import { telaEquipe } from './telas/equipe';
import { telaIncorporadoras } from './telas/incorporadoras';
import { telaEmpreendimentos } from './telas/empreendimentos';
import { telaEmpreendimento } from './telas/empreendimento';
import { telaDocumentos } from './telas/documentos';
import { telaCenario } from './telas/cenario';
import { telaRelatorio } from './relatorio/pagina';
import { CAMINHO_RELATORIO } from './relatorio/endereco';
import { criarCliente } from '../../../packages/dados/src/cliente';
import { aoMudarAutenticacao, obterEstadoSessao, sair } from '../../../packages/dados/src/sessao';
import type { Perfil } from '../../../packages/dados/src/sessao';

const raiz = document.getElementById('raiz');
if (raiz === null) throw new Error('A âncora #raiz não existe no index.html.');

iniciarTema();
iniciarIds();

/* Faixa de ambiente: aparece em TODA tela, inclusive na de entrada e na de convite.
   Um endereço público que parece produção e é o banco de desenvolvimento acaba
   recebendo dado real de cliente — e dado real não pode entrar num banco onde as
   ferramentas de validação criam e apagam registros a cada rodada. Dizer isso na tela
   é a proteção mais barata que existe. */
if (ehDemonstracao()) {
  document.body.classList.add('tem-faixa-ambiente');
  const faixa = h('div', { class: 'faixa-ambiente' },
    h('b', {}, 'Ambiente de demonstração'),
    ' — os dados aqui são de teste e podem ser apagados a qualquer momento.',
    /* Desde 29/07/2026 dá para anexar ARQUIVO, e arquivo de cliente é outro patamar:
       não é um número que alguém redigita, é um documento que veio de fora. O aviso
       precisa dizer isso com todas as letras enquanto o banco for o de desenvolvimento. */
    ' Não anexe documento real de cliente.');
  document.body.appendChild(faixa);
  /* Lote 4 de 10/10/2026: a faixa cobria o pé da barra lateral (o selo "Admin Hype" ficava metade debaixo
     dela). A barra reserva a altura da faixa (estilo.css, seção 23) — MEDIDA, porque numa janela estreita
     o texto quebra em duas linhas e um número fixo voltaria a cobrir. */
  const medirFaixa = (): void => {
    document.documentElement.style.setProperty('--faixa-amb-h', `${Math.ceil(faixa.getBoundingClientRect().height)}px`);
  };
  medirFaixa();
  if (typeof ResizeObserver === 'function') new ResizeObserver(medirFaixa).observe(faixa);
}

/* ============================================================================
 * Estado do arranque
 * ========================================================================== */

let casca: Casca | null = null;
let perfilAtual: Perfil | null = null;
/** Cresce a cada pedido de desenho: resposta de tela antiga não sobrescreve a nova. */
let geracao = 0;

function trocarRaiz(no: Node): void {
  limpar(raiz!);
  raiz!.appendChild(no);
}

function derrubarCasca(): void {
  casca?.destruir();
  casca = null;
  perfilAtual = null;
}

/* ============================================================================
 * Desenho por rota
 * ========================================================================== */

async function vistaDaRota(perfil: Perfil, r: Rota) {
  switch (r.nome) {
    case 'incorporadoras':
    case 'entrar':
      return telaIncorporadoras(perfil);
    case 'empreendimentos':
      return telaEmpreendimentos(perfil, r.empresaId);
    case 'empreendimento':
      /* Resolve para o cenário padrão (trocando o endereço) ou mostra o estado vazio —
         a lista de cenários deixou de ser tela em 09/10/2026. */
      return telaEmpreendimento(perfil, r.empresaId, r.empreendimentoId);
    case 'documentos':
      return telaDocumentos(perfil, r.empresaId);
    case 'cenario':
      return telaCenario(perfil, r.cenarioId);
    case 'equipe':
      return telaEquipe(perfil, r.empresaId);
    case 'pedidos':
      return telaPedidos(perfil);
    case 'convite':
      /* Quem já entrou e abre um link de convite vai para a raiz: aceitar de novo não faz
         sentido, e o cadastro exige que NÃO haja sessão. */
      return telaIncorporadoras(perfil);
  }
}

/* ============================================================================
 * A TELA PRETA (07/10/2026) — nenhuma falha do desenho fica sem dono
 *
 * O projeto de dados pausou por inatividade e o Fincast abriu como um retângulo preto,
 * sem frase nenhuma: `obterEstadoSessao()` lança quando o servidor não responde, ela era
 * chamada fora de qualquer `try`, e todo mundo chamava `desenhar()` com `void`. A
 * rejeição não tinha dono e o `#raiz` ficava vazio — com o tema escuro, preto.
 *
 * Agora TODO chamador passa por aqui (navegação, troca de sessão, botão de tentar de novo,
 * arranque), e `protegido()` garante que a falha vire tela com frase. A regra é testada
 * em `tests/arranque.test.ts`; a tela, em `tools/dev/validar-servidor-fora.mjs`.
 * ========================================================================== */
async function desenhar(): Promise<void> {
  let minha = 0;
  await protegido(
    () => {
      const p = desenharSemProtecao(); // o `++geracao` roda síncrono, antes do 1º await
      minha = geracao;
      return p;
    },
    (f, erro) => {
      console.error('[Fincast] a tela não conseguiu abrir:', erro);
      if (minha !== geracao) return; // uma tentativa mais nova já está desenhando
      derrubarCasca();
      trocarRaiz(telaDeFalhaDeArranque(f));
    },
  );
}

/** Centraliza um cartão na janela inteira — a casca ainda não existe nestes momentos. */
function telaCheiaCentral(...filhos: Node[]): HTMLElement {
  return h('div', { style: 'min-height:100vh;display:grid;place-items:center;padding:24px' }, ...filhos);
}

/** O arranque. Sem isto, até o servidor responder (ou o prazo de 30 s vencer) a tela é só fundo. */
function telaConectando(): HTMLElement {
  return telaCheiaCentral(carregando('o Fincast'));
}

function telaDeFalhaDeArranque(f: FalhaDeArranque): HTMLElement {
  /* Âmbar quando é o servidor (ninguém errou nada, é esperar); vermelho quando é outra coisa. */
  const cor = f.servidorFora ? 'hsl(var(--warning))' : 'hsl(var(--destructive))';
  return telaCheiaCentral(
    h(
      'div',
      {
        class: 'card',
        role: 'alert',
        style: 'padding:30px 28px;max-width:52ch;display:grid;gap:14px;justify-items:center;text-align:center',
      },
      h('span', { style: `color:${cor}`, html: icone.alerta(28) }),
      h('h2', { style: 'margin:0;font-size:var(--fs-lg);font-weight:660' }, f.titulo),
      h('p', { class: 'muted', style: 'margin:0;line-height:1.55' }, f.mensagem),
      h(
        'button',
        {
          class: 'btn pri',
          onclick: () => {
            trocarRaiz(telaConectando());
            void desenhar();
          },
        },
        'Tentar de novo',
      ),
    ),
  );
}

async function desenharSemProtecao(): Promise<void> {
  const meu = ++geracao;
  const estado = await obterEstadoSessao();
  /* Um desenho mais novo começou enquanto este perguntava pela sessão (o F5 desenha duas vezes:
     a chamada do arranque e o INITIAL_SESSION que a autenticação repassa): este desiste ANTES de
     construir a vista — construir custa idas ao servidor e, até a revisão de 09/10/2026, ligava
     efeitos que a vista descartada nunca desligava (ver `Vista.aoMostrar`, a correção de fundo). */
  if (meu !== geracao) return;

  /* O convite é a ÚNICA tela que existe sem sessão, e por isso vem antes de tudo: quem
     abre o link ainda não tem conta, e mandá-lo para a tela de entrada seria um beco —
     ele não tem senha para digitar. */
  const convite = lerConviteDaUrl();
  if (convite !== null && estado.situacao === 'anonimo') {
    if (meu !== geracao) return;
    derrubarCasca();
    trocarRaiz(telaConvite(convite, () => {
      /* Entrou: sai do endereço do convite para a raiz, SUBSTITUINDO o histórico — o
         token não deve ficar no botão "voltar". */
      window.history.replaceState(null, '', '/');
      void desenhar();
    }));
    return;
  }

  if (estado.situacao === 'anonimo') {
    if (meu !== geracao) return;
    derrubarCasca();
    trocarRaiz(telaEntrada(() => void desenhar()));
    return;
  }

  if (estado.situacao === 'sem_perfil') {
    if (meu !== geracao) return;
    derrubarCasca();
    trocarRaiz(telaSemPerfil(estado.email, () => void sair()));
    return;
  }

  const perfil = estado.perfil;

  /* O RELATÓRIO COMPARATIVO (EL-197, 09/10/2026) é desenhado SEM a casca, como o convite:
     abre em aba própria, imprime limpo (sem barra lateral nem a superior) e o endereço pode ser
     mandado. Precisa de sessão — por isso vem depois da entrada, e quem chega sem ela entra e
     cai de volta aqui (o endereço não muda). O endereço carrega a escolha em `?c=…&obj=…`,
     que o roteador da casca descarta: a página lê `location.search` (relatorio/endereco.ts). */
  if (window.location.pathname === CAMINHO_RELATORIO) {
    if (meu !== geracao) return;
    derrubarCasca();
    trocarRaiz(telaCheiaCentral(carregando('o relatório')));
    const pagina = await telaRelatorio(perfil);
    if (meu !== geracao) return;
    trocarRaiz(pagina);
    return;
  }
  /* saindo do relatório pelo histórico da aba: o modo de impressão dele não vale aqui */
  document.documentElement.classList.remove('pg-relatorio');

  if (casca === null || perfilAtual?.id !== perfil.id) {
    derrubarCasca();
    casca = criarCasca(perfil);
    perfilAtual = perfil;
    trocarRaiz(casca.raiz);
  }

  /* Quem entrou e ainda está em /entrar não tem para onde ir sozinho: manda para a raiz
     SUBSTITUINDO a entrada do histórico, senão o botão "voltar" devolve à tela de login
     de um usuário que já está logado. */
  const r = rotaAtual();
  if (r.nome === 'entrar') {
    ir({ nome: 'incorporadoras' }, { substituir: true });
    return;
  }

  const cascaViva = casca;
  cascaViva.definirVista({ trilha: [{ texto: '…' }], corpo: carregando('os dados'), provisoria: true });

  try {
    const vista = await vistaDaRota(perfil, r);
    if (meu !== geracao || casca !== cascaViva) return;
    cascaViva.definirVista(vista);
  } catch (e) {
    if (meu !== geracao || casca !== cascaViva) return;
    cascaViva.definirVista({
      trilha: [{ texto: 'Erro' }],
      corpo: falha(e, () => void desenhar()),
    });
  }
}

/* ============================================================================
 * Ligar
 * ========================================================================== */

try {
  criarCliente(configSupabase());
} catch (e) {
  const msg = e instanceof ConfigAusente ? e.message : `Não foi possível iniciar o Fincast: ${String(e)}`;
  trocarRaiz(
    h('div', { style: 'min-height:100vh;display:grid;place-items:center;padding:24px' },
      h('div', { class: 'card', style: 'padding:28px;max-width:52ch;text-align:center' }, msg)),
  );
  throw e;
}

iniciarRoteador();
aoNavegar(() => void desenhar());

/* Sair numa aba tem de refletir nas outras: o Supabase avisa a troca de autenticação e é
   isso que devolve a tela de entrada sem o usuário precisar recarregar. */
aoMudarAutenticacao(() => {
  void desenhar();
});

trocarRaiz(telaConectando());
void desenhar();
